import PropTypes from 'prop-types';

import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import { FieldSet, Flex, Form, Heading, Paragraph, VisuallyHidden } from '@zillow/constellation';
import { Field, Form as ReactFinalForm } from 'react-final-form';
import { BECOME_APPLICATION_MANAGER_MUTATION } from '../../../utils/businessVerification';
import { useMutation } from '@apollo/client';
import LabeledInput from '../../LabeledInput';
import { ERROR_STEP } from '../ApplicationManagerModal.util';

const INVALID_MSG = 'Please enter a value';
const MAX_LENGTH = 128;

/**
 * This helper makes it such that the mutation actually throws if there is an error. Usually it would return a
 * resolved promise, which doesn't interact well with other libraries that expect a rejected promise for an error
 * (final-form and the wizard framework both do)
 * @returns The becomeApplicationManager mutation as a function, that throws an error if there are errors returned
 */
function useOnSubmit() {
  const [becomeApplicationManager] = useMutation(BECOME_APPLICATION_MANAGER_MUTATION);
  return async (values) => {
    const { errors } = await becomeApplicationManager({ variables: values });
    if (errors) {
      throw new Error(errors);
    }
  };
}

export default function NameStep({ id }) {
  const { onRequestNext, onRequestStep } = useWizard();
  const onSubmit = useOnSubmit();

  return (
    <ReactFinalForm
      onSubmit={onSubmit}
      render={({ form }) => (
        <WizardStep
          id={id}
          label="Name"
          onRequestNext={async (navigationController) => {
            try {
              await form.submit();
            } catch {
              // made the request, and it failed
              navigationController.abort();
              onRequestStep(ERROR_STEP);
            }
            // never made the request in the first place, validation errors
            // We still want to "attempt" the submit though, so all fields show the errors
            if (form.getState().submitFailed) {
              navigationController.abort();
            }
          }}
        >
          <Heading level={4} as="h1" marginBottom="sm">
            Contact Name
          </Heading>
          <Paragraph marginBottom="xl">
            A contact must be displayed with the listing because renters are legally entitled to see who has access to
            their application.
          </Paragraph>
          <Form
            onSubmit={async (e) => {
              // prevents default form behaviour
              e.preventDefault();
              e.stopPropagation();
              // Letting the wizard framework handle both submission and navigation
              onRequestNext();
            }}
          >
            <FieldSet legend={<VisuallyHidden as="legend">Name</VisuallyHidden>}>
              <Flex display="flex" gap="sm">
                <Flex flex="1">
                  <Field
                    name="firstName"
                    id="first-name"
                    label="First name"
                    validate={(value) => (value && value.length < MAX_LENGTH ? null : INVALID_MSG)}
                    component={LabeledInput}
                  />
                </Flex>
                <Flex flex="1">
                  <Field
                    name="lastName"
                    id="last-name"
                    label="Last name"
                    validate={(value) => (value && value.length < MAX_LENGTH ? null : INVALID_MSG)}
                    component={LabeledInput}
                  />
                </Flex>
              </Flex>
            </FieldSet>
            <VisuallyHidden as="button" type="submit" id="submit-name-form">
              Submit
            </VisuallyHidden>
          </Form>
        </WizardStep>
      )}
    />
  );
}

NameStep.propTypes = {
  id: PropTypes.string.isRequired,
};
